import { useEffect, useState } from "react";
import axios from "axios";
import { baseURL, PUBLIC_EXCELLENT_STUDENTS } from "../../../Api/Api";
import "./Sections.css";
import "./ExcellentSection.css";

const api = axios.create({ baseURL });

function parseAverage(raw) {
  if (raw == null) return null;
  if (typeof raw === "string" && raw.includes("/")) {
    const [full, obtained] = raw.split("/").map((v) => parseFloat(v));
    if (!isNaN(full) && !isNaN(obtained) && full > 0) {
      return { full, obtained, percent: (obtained / full) * 100 };
    }
    return null;
  }
  const num = parseFloat(raw);
  return isNaN(num) ? null : { full: 100, obtained: num, percent: num };
}

function formatNum(n) {
  return n % 1 === 0 ? n : n.toFixed(1);
}

function parseSubjects(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];
  try {
    const p = JSON.parse(raw);
    if (Array.isArray(p)) return p;
  } catch {}
  return raw.split(/[،,]/).map((s) => s.trim()).filter(Boolean).map((s) => ({ name: s, grade: null }));
}

const CAP_STYLES = [
  { bg: "linear-gradient(135deg,#b8860b 0%,#d4af37 45%,#ffd700 100%)", label: "الأول" },
  { bg: "linear-gradient(135deg,#6b7280 0%,#9ca3af 45%,#d1d5db 100%)", label: "الثاني" },
  { bg: "linear-gradient(135deg,#92400e 0%,#b45309 45%,#d97706 100%)", label: "الثالث" },
];
const DEFAULT_CAP = { bg: "linear-gradient(135deg,#1e3a5f 0%,#2d5a8e 100%)", label: null };

/* ─── Single card ─── */
function ExcellentCard({ student, rank }) {
  const [expanded, setExpanded] = useState(false);

  const photoUrl  = student.photo ? `${baseURL}/uploads/students/${student.photo}` : null;
  const className = student.Class?.name || student.class?.name || null;
  const average   = parseAverage(student.final_average);
  const subjects  = parseSubjects(student.excellent_subjects);
  const capStyle  = CAP_STYLES[rank] ?? DEFAULT_CAP;
  const rankLabel = capStyle.label ?? `#${rank + 1}`;

  const rankIconMap = ["🥇", "🥈", "🥉"];
  const rankIcon    = rankIconMap[rank] ?? null;

  return (
    <div className={`es-card ${rank < 3 ? `es-card-top es-card-rank${rank}` : ""}`}>
      {/* Cap */}
      <div className="es-cap" style={{ background: capStyle.bg }}>
        <span className="es-rank-label">{rankIcon || rankLabel}</span>
        {rank < 3 && <div className="es-cap-shine" />}
      </div>

      {/* Avatar */}
      <div className="es-avatar-wrap">
        {photoUrl ? (
          <img className="es-avatar" src={photoUrl} alt={student.name} />
        ) : (
          <div className="es-avatar-fallback">
            <svg width="34" height="34" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="es-body">
        <h3 className="es-name">{student.name}</h3>

        {className && <span className="es-class-badge">{className}</span>}

        {/* Average */}
        {average != null ? (
          <button
            className={`es-avg-btn ${expanded ? "es-avg-btn-open" : ""}`}
            onClick={() => subjects.length > 0 && setExpanded((v) => !v)}
            style={{ cursor: subjects.length > 0 ? "pointer" : "default" }}
            title={subjects.length > 0 ? "اضغط لعرض المواد" : ""}
          >
            <span className="es-avg-value">{formatNum(average.obtained)}</span>
            <span className="es-avg-slash">/</span>
            <span className="es-avg-total">{formatNum(average.full)}</span>
            {subjects.length > 0 && (
              <svg
                className={`es-avg-arrow ${expanded ? "es-avg-arrow-up" : ""}`}
                width="12" height="12" fill="currentColor" viewBox="0 0 24 24"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            )}
          </button>
        ) : subjects.length > 0 ? (
          <button
            className={`es-subjects-toggle ${expanded ? "open" : ""}`}
            onClick={() => setExpanded((v) => !v)}
          >
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            المواد المتفوق فيها
          </button>
        ) : null}

        {/* Subjects expand */}
        {expanded && subjects.length > 0 && (
          <div className="es-subjects">
            {subjects.map((sub, i) => {
              const name  = typeof sub === "string" ? sub : sub.name;
              const grade = typeof sub === "object" ? sub.grade : null;
              const gradeLabel =
                grade != null
                  ? (typeof grade === "string" && grade.includes("/") ? grade : `${grade}%`)
                  : null;
              return (
                <span key={i} className="es-subject-tag">
                  {name}
                  {gradeLabel != null && <span className="es-subject-grade">{gradeLabel}</span>}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Skeleton ─── */
function SkeletonCard() {
  return (
    <div className="es-card es-card-skel">
      <div className="es-cap es-cap-skel" />
      <div className="es-avatar-wrap es-avatar-skel" />
      <div className="es-body">
        <div className="es-sk-line es-sk-lg" />
        <div className="es-sk-line es-sk-sm" />
        <div className="es-sk-line es-sk-md" />
      </div>
    </div>
  );
}

/* ─── Group by class ─── */
function groupByClass(students) {
  const map = {};
  students.forEach((s) => {
    const cls  = s.Class || s.class;
    const id   = cls?.id ?? "none";
    const name = cls?.name ?? "غير محدد";
    if (!map[id]) map[id] = { name, students: [] };
    map[id].students.push(s);
  });
  Object.values(map).forEach((g) => {
    g.students.sort(
      (a, b) => (parseAverage(b.final_average)?.percent ?? -1) - (parseAverage(a.final_average)?.percent ?? -1)
    );
  });
  return Object.values(map);
}

/* ─── Main Section ─── */
export default function ExcellentStudentsSection() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get(PUBLIC_EXCELLENT_STUDENTS)
      .then((res) => setStudents(res.data.students || res.data || []))
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && students.length === 0) return null;

  const groups = groupByClass(students);
  const multiClass = groups.length > 1;

  return (
    <section className="section-wrapper light" id="excellent-students">
      <div className="container">
        <div className="section-header">
          <span className="section-badge badge-gold">أوائل الطلاب</span>
          <h2 className="section-title">الطلاب المتميزون</h2>
          <p className="section-subtitle">
            نفخر بتقديم أبرز طلابنا المتميزين الذين حققوا نتائج استثنائية.
          </p>
          <div className="section-divider divider-gold" />
        </div>

        {loading ? (
          <div className="es-grid">
            {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.name} className="es-group">
              {multiClass && (
                <div className="es-group-header">
                  <span className="es-group-icon">🏫</span>
                  <span className="es-group-name">{group.name}</span>
                  <span className="es-group-count">{group.students.length} طالب</span>
                </div>
              )}
              <div className="es-grid">
                {group.students.map((s, i) => (
                  <ExcellentCard key={s.id} student={s} rank={i} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
